/**
 * Every route path in one object.
 *
 * The URLs are Dutch because they are part of the interface the user sees and
 * shares. Referring to them through this object rather than as string literals
 * means renaming a path is one edit, not a search-and-replace.
 */
/*
 * Three constants used to live here that no `<Route>` ever matched —
 * `myProgramme` (`/mijn-programma`), `weekPlan` (`/weekplan`) and
 * `settingsProfile` (`/instellingen/profiel`). Nothing linked to them, and
 * anything that had would have landed on the 404 page. A route table that
 * contains destinations the router does not serve is worse than no table: it
 * reads as a list of places you can send somebody.
 */
/*
 * Every id in a path is a `string`. These four took `number | string` while ids
 * were integers; the union is gone so a caller holding a number now has to say
 * so, which is what caught the one place still typed that way — see
 * `TrainingCalendar`.
 */
export const routes = {
    dashboard: '/',

    // Authentication
    login: '/inloggen',
    register: '/registreren',
    forgotPassword: '/wachtwoord-vergeten',
    resetPassword: '/wachtwoord-herstellen',
    verifyEmail: '/e-mailadres-bevestigen',

    // Onboarding
    onboarding: '/welkom',

    // Training
    workouts: '/trainingen',

    /*
     * Sits under /trainingen as a static segment. React Router ranks a literal
     * segment above a dynamic one, so this never collides with the detail
     * route's `:slug` — but the two must stay in that order conceptually: a
     * workout may never be given the slug "nieuw".
     */
    workoutBuilder: '/trainingen/nieuw',
    workout: (slug: string) => `/trainingen/${slug}`,
    workoutDetailPattern: '/trainingen/:slug',
    workoutEditor: (slug: string) => `/trainingen/${slug}/aanpassen`,
    workoutEditorPattern: '/trainingen/:slug/aanpassen',
    player: (sessionId: string) => `/training/${sessionId}`,
    playerPattern: '/training/:sessionId',
    sessionSummary: (sessionId: string) => `/training/${sessionId}/afgerond`,
    sessionSummaryPattern: '/training/:sessionId/afgerond',

    // Programmes — the layer above a workout: a run of weeks that builds up.
    programmes: '/programmas',
    /*
     * A literal segment under /programmas. React Router ranks a literal above a
     * dynamic one, so this never collides with the detail route's `:programmeId`.
     */
    programmeBuilder: '/programmas/nieuw',
    programme: (id: string) => `/programmas/${id}`,
    programmeEditor: (id: string) => `/programmas/${id}/aanpassen`,
    programmeEditorPattern: '/programmas/:programmeId/aanpassen',
    programmeDetailPattern: '/programmas/:programmeId',

    // Handbook
    exercises: '/oefeningen',
    exercise: (slug: string) => `/oefeningen/${slug}`,
    exerciseDetailPattern: '/oefeningen/:slug',

    // Progress
    progress: '/voortgang',
    measurements: '/metingen',
    history: '/geschiedenis',

    // Setup
    equipment: '/materiaal',

    // Settings
    settings: '/instellingen',
    settingsReminders: '/instellingen/herinneringen',
    settingsIntegrations: '/instellingen/koppelingen',
} as const;
